import styled, { css } from 'styled-components';
import { colors, fonts, fontSize, fontWeight, media, radius, rgba, shadow, spacer } from 'settings/style';

const telegramMessageTextStyles = css`
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;
  unicode-bidi: plaintext;
  text-align: start;
`;

export const CommunityPage = styled.div`
  min-height: 100vh;
  padding: 1.5rem 0 5rem;

  ${media.md`
    padding: 0.7rem 0 7rem;
  `}
`;

export const CommunityHero = styled.section`
  margin-bottom: ${spacer.xl};
`;

export const CommunityTitle = styled.h1`
  margin: 0.4rem 0 0.35rem;
  font-size: 2rem;
  font-family: ${fonts.tertiary};
  font-weight: 500;
  color: #1a2135;
`;

export const CommunityLayout = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 2.1fr) minmax(280px, 0.9fr);
  gap: ${spacer.xl};
  align-items: start;

  ${media.lg`
    grid-template-columns: 1fr;
  `}
`;

export const FeedColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacer.lg};
`;

export const SidebarColumn = styled.aside`
  position: sticky;
  top: 7rem;
  display: flex;
  flex-direction: column;
  gap: ${spacer.lg};

  ${media.lg`
    position: static;
  `}
`;

export const CommunityPanel = styled.div`
  padding: ${spacer.xl};
  border: 1px solid ${colors.cardBorder};
  border-radius: ${radius.lg};
  background: ${rgba(colors.white, 0.92)};
  box-shadow: ${shadow.sm};
  ${media.md`
    padding: ${spacer.lg};
  `}
`;

export const FilterHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${spacer.md};
  margin-bottom: ${spacer.md};

  ${media.md`
    flex-direction: column;
    align-items: stretch;
  `}
`;

export const FilterTitle = styled.h2`
  margin: 0;
  color: ${colors.gray900};
  font-size: 1.35rem;
  font-family: ${fonts.tertiary};
`;

export const SearchInputWrap = styled.div`
  min-width: 18rem;
  
  .ant-input-affix-wrapper {
    border-radius: ${radius.lg} !important;
    border-color: ${colors.gray300};
    padding: 0.3rem 0.95rem;
    box-shadow: none;
  }

  .ant-input-affix-wrapper:focus,
  .ant-input-affix-wrapper-focused {
    border-color: ${colors.primary};
  }

  ${media.md`
    min-width: 0;
  `}
`;

export const FilterActions = styled.div`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: ${spacer.xs};

  ${media.md`
    margin-left: 0;
    width: 100%;
    justify-content: flex-end;
  `}
`;

export const TranslateToggleButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${spacer.xs};
  border: 1px solid ${colors.gray300};
  border-radius: ${radius.md};
  background: ${colors.white};
  color: ${colors.gray800};
  height: 2.2rem;
  padding: 0 ${spacer.sm};
  cursor: pointer;
  transition: border-color 0.2s ease, color 0.2s ease;

  &:hover {
    border-color: ${colors.primary};
    color: ${colors.primary500};
  }
`;

export const FeedList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacer.md};
`;

export const FeedPaginationWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacer.md};
  margin-top: ${spacer.lg};

.ant-pagination-item,
  .ant-pagination-prev .ant-pagination-item-link,
  .ant-pagination-next .ant-pagination-item-link {
    border-radius: 0;
    color: ${colors.grey900} !important;
  }

  .ant-pagination-item-active {
    background: ${colors.grey100};
    border: 0px;
    border-radius: 5px;

    a {
      color: ${colors.grey900};
    }
  }

  .ant-pagination-item-active:hover {
    a {
      color: ${colors.grey900};
    }
  }

  .ant-pagination-item-link {
    color: ${colors.grey900};
    border-color: ${colors.grey900};

    .anticon {
      color: ${colors.grey900};
    }
  }

  .ant-pagination-prev:not(.ant-pagination-disabled):hover .ant-pagination-item-link,
  .ant-pagination-next:not(.ant-pagination-disabled):hover .ant-pagination-item-link {
    border-color: ${colors.grey900};
    color: ${colors.grey900};
  }

  .ant-pagination-jump-prev .ant-pagination-item-container .ant-pagination-item-link-icon,
  .ant-pagination-jump-next .ant-pagination-item-container .ant-pagination-item-link-icon {
    color: ${colors.grey900};
  }  
`;

export const FeedPaginationSummary = styled.div`
  color: ${colors.gray600};
  font-size: ${fontSize.sm};
`;

export const LoadMoreButton = styled.button`
  border: 1px solid ${colors.primary};
  border-radius: ${radius.xl};
  background: ${colors.white};
  color: ${colors.primary500};
  padding: 0.75rem 1.2rem;
  font-weight: ${fontWeight.bold};
  cursor: pointer;
  transition: 0.2s ease;

  &:hover {
    background: ${rgba(colors.primary, 0.12)};
  }
`;

export const FeedCard = styled.article`
  display: flex;
  align-items: flex-start;
  gap: ${spacer.md};
  padding: ${spacer.md};
  border-radius: ${radius.lg};
  background: transparent;
  box-shadow: none;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${rgba(colors.primary, 0.03)};
  }
`;

export const FeedCardContent = styled.div`
  flex: 1;
  min-width: 0;
`;

export const FeedCardHeader = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: ${spacer.md};
`;

export const FeedMeta = styled.div`
  min-width: 0;
  flex: 1;
  padding-top: 0.1rem;
`;

export const FeedAuthorRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacer.xs} ${spacer.sm};
  align-items: center;
  margin-top: 0.1rem;
`;

export const FeedAuthor = styled.span`
  color: ${colors.gray900};
  font-weight: ${fontWeight.bolder};
  font-size: ${fontSize.base};
`;

export const FeedTime = styled.span`
  color: ${colors.gray600};
  white-space: nowrap;
`;

export const FeedBody = styled.p<{ $rtl?: boolean }>`
  ${telegramMessageTextStyles}
  margin: ${spacer.xs} 0 0;
  color: ${colors.gray800};
  font-size: ${fontSize.base};
  line-height: 1.75;
  background-color: ${colors.grey200};
  border-radius: ${radius.md};
  border: 1px solid ${colors.cardBorder};
  padding: ${spacer.sm};
  direction: ${({ $rtl }) => ($rtl ? 'rtl' : 'auto')};
`;

export const FeedBodyTranslation = styled.p`
  ${telegramMessageTextStyles}
  margin: ${spacer.xs} 0 0;
  color: ${colors.gray700};
  font-size: ${fontSize.sm};
  line-height: 1.65;
  background-color: ${colors.white};
  border-radius: ${radius.md};
  border: 1px solid ${colors.cardBorder};
  padding: ${spacer.sm};
  direction: ltr;
`;

export const CommentSection = styled.div`
  margin-top: ${spacer.sm};
`;

export const CommentToggleButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: transparent;
  color: ${colors.gray600};
  font-size: ${fontSize.sm};
  font-weight: ${fontWeight.bold};
  padding: 0;
  cursor: pointer;

  &:hover {
    color: ${colors.primary500};
  }
`;

export const CommentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacer.sm};
  margin-top: ${spacer.sm};
`;

export const CommentItem = styled.div`
  padding: ${spacer.sm};
  border-radius: ${radius.md};
  background: ${colors.white};
  border: 1px solid ${colors.cardBorder};
`;

export const CommentItemHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacer.sm};
  margin-bottom: 4px;
`;

export const CommentAuthor = styled.span`
  color: ${colors.gray900};
  font-size: ${fontSize.sm};
  font-weight: ${fontWeight.bold};
`;

export const CommentTime = styled.span`
  color: ${colors.gray600};
  font-size: 0.75rem;
  white-space: nowrap;
`;

export const CommentItemMeta = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

export const CommentContent = styled.p<{ $rtl?: boolean }>`
  ${telegramMessageTextStyles}
  margin: 0;
  color: ${colors.gray800};
  font-size: ${fontSize.sm};
  line-height: 1.5;
  direction: ${({ $rtl }) => ($rtl ? 'rtl' : 'auto')};
`;

export const CommentDeleteButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: ${radius.sm};
  background: transparent;
  color: ${colors.gray600};
  font-size: 0.85rem;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    color: ${colors.danger};
    background: ${rgba(colors.danger, 0.08)};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const CommentForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${spacer.xs};
  margin-top: ${spacer.sm};
`;

export const CommentTextarea = styled.textarea`
  width: 100%;
  min-height: 72px;
  resize: vertical;
  border: 1px solid ${colors.cardBorder};
  border-radius: ${radius.md};
  padding: ${spacer.sm};
  font-size: ${fontSize.sm};
  line-height: 1.5;
  color: ${colors.gray800};
  background: ${colors.white};

  &:focus {
    outline: none;
    border-color: ${colors.primary};
    box-shadow: 0 0 0 2px ${rgba(colors.primary, 0.15)};
  }
`;

export const CommentFormActions = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export const CommentSubmitButton = styled.button`
  border: none;
  border-radius: ${radius.md};
  background: ${colors.primary500};
  color: ${colors.white};
  font-size: ${fontSize.sm};
  font-weight: ${fontWeight.bold};
  padding: 0.45rem 0.9rem;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const CommentLoginHint = styled.p`
  margin: ${spacer.sm} 0 0;
  color: ${colors.gray600};
  font-size: ${fontSize.sm};
`;

export const CommentLoading = styled.div`
  margin-top: ${spacer.sm};
  color: ${colors.gray600};
  font-size: ${fontSize.sm};
`;

export const EmptyState = styled.div`
  padding: 3rem 1.5rem;
  border: 1px dashed ${colors.gray400};
  border-radius: ${radius.lg};
  background: ${rgba(colors.white, 0.6)};
  text-align: center;
  color: ${colors.gray700};
`;

export const SidebarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${spacer.sm};
  margin-bottom: ${spacer.sm};
`;

export const SidebarTitle = styled.h3`
  margin: 0;
  color: ${colors.gray900};
  font-size: ${fontSize.md};
  font-family: ${fonts.tertiary};
`;

export const GroupFilterList = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: ${spacer.md};
  border-bottom: 1px solid ${colors.gray300};
  padding-bottom: ${spacer.sm};
  margin-bottom: ${spacer.md};
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${rgba(colors.gray400, 0.6)};
    border-radius: 999px;
  }
`;

export const GroupFilterButton = styled.button<{ $active: boolean }>`
  white-space: nowrap;
  border: none;
  background: transparent;
  color: ${({ $active }) => ($active ? colors.primary700 : colors.gray700)};
  font-size: ${fontSize.md};
  font-weight: ${({ $active }) => ($active ? fontWeight.bolder : fontWeight.bold)};
  padding: 0.15rem 0;
  border-bottom: 2px solid ${({ $active }) => ($active ? colors.primary : 'transparent')};
  cursor: pointer;
  transition: color 0.2s ease, border-color 0.2s ease;

  &:hover {
    color: ${colors.primary700};
  }
`;

export const GroupFilterThumb = styled.img`
  width: 1.6rem;
  height: 1.6rem;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

export const StatsList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${spacer.md};
`;

export const StatCard = styled.div`
  padding: ${spacer.md};
  border-radius: ${radius.md};
  background: ${colors.white300};
`;

export const StatLabel = styled.div`
  color: ${colors.gray600};
  font-size: ${fontSize.sm};
`;

export const StatValue = styled.div`
  margin-top: ${spacer.xs};
  color: ${colors.gray900};
  font-size: 1.5rem;
  font-weight: ${fontWeight.bolder};
`;

// ─── Folder create row (sidebar) ────────────────────────────────────────────

export const FolderCreateRow = styled.div`
  display: flex;
  gap: ${spacer.xs};
  margin-bottom: ${spacer.md};
`;

export const FolderInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 0.45rem 0.75rem;
  border: 1px solid ${colors.gray300};
  border-radius: ${radius.md};
  font-size: ${fontSize.sm};
  outline: none;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: ${colors.primary};
  }
`;

export const FolderCreateButton = styled.button`
  flex-shrink: 0;
  width: 2.1rem;
  height: 2.1rem;
  display: grid;
  place-items: center;
  border: 1px solid ${colors.primary};
  border-radius: 50%;
  background: ${colors.grey900};
  color: white;
  cursor: pointer;
  transition: 0.2s ease;
  font-size: ${fontSize.sm};

  &:hover {
    background: ${rgba(colors.primary500, 0.9)};
  }
`;

export const FolderAddButton = styled.button`
  padding: 0.7rem 0.75rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacer.xs};
  border-radius: ${radius.lg};
  background: ${colors.grey900};
  color: ${colors.white};
  font-weight: ${fontWeight.bold};
  cursor: pointer;
`;

export const FolderFilterList = styled.div`
  margin-top: ${spacer.lg};
  display: flex;
  flex-direction: column;
  gap: ${spacer.lg};
`;

export const FolderFilterButtonWrap = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid ${({ $active }) => ($active ? colors.primary : rgba(colors.gray300, 0.6))};
  background: ${({ $active }) => ($active ? colors.grey200 : colors.white)};
  box-shadow: ${({ $active }) => ($active ? shadow.sm : 'none')};
  border-radius: ${radius.lg};
  padding: 0.7rem 0.75rem;
  transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: ${colors.primary};
    background: ${rgba(colors.primary, 0.06)};
  }
`;

export const FolderFilterButton = styled.button<{ $active: boolean }>`
  flex: 1;
  text-align: left;
  border: none;
  background: transparent;
  color: ${colors.grey900};
  font-size: ${fontSize.md};
  padding: 0.1rem 0;
  cursor: pointer;
  transition: color 0.2s ease;
`;

export const FolderDeleteButton = styled.button`
  display: grid;
  place-items: center;
  width: 1.25rem;
  height: 1.25rem;
  padding: 0;
  border: none;
  border-radius: 50 %;
  background: transparent;
  color: ${colors.gray500};
  cursor: pointer;
  font-size: 0.7rem;
  transition: 0.2s ease;

  &:hover {
    background: ${rgba(colors.danger, 0.12)};
    color: ${colors.danger};
  }
`;

export const FolderBadge = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.25rem 0.65rem;
  border-radius: ${radius.lg};
  border: 1px solid ${rgba(colors.primary, 0.35)};
  background: ${rgba(colors.primary, 0.08)};
  color: ${colors.primary500};
  font-size: ${fontSize.sm};
  font-weight: ${fontWeight.bold};
  cursor: pointer;
  transition: 0.2s ease;

  &:hover {
    background: ${rgba(colors.primary, 0.18)};
  }
`;

export const AssignFolderButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.25rem 0.65rem;
  border-radius: ${radius.lg};
  border: 1px dashed ${colors.gray400};
  background: transparent;
  color: ${colors.gray600};
  font-size: ${fontSize.sm};
  cursor: pointer;
  transition: 0.2s ease;

  &:hover {
    border-color: ${colors.primary};
    border-style: solid;
    color: ${colors.primary500};
  }
`;

export const MobileHideLabel = styled.span`
  ${media.md`
    display: none;
  `}
`;

export const FolderPickerWrap = styled.div`
  position: absolute;
  top: calc(100% + 0.4rem);
  right: 0;
  min-width: 11rem;
  border: 1px solid ${rgba(colors.primary, 0.18)};
  border-radius: ${radius.md};
  background: ${colors.white};
  box-shadow: ${shadow.md};
  z-index: 50;
  overflow: hidden;
`;

export const FolderPickerItem = styled.button<{ $active: boolean }>`
  display: block;
  width: 100 %;
  text-align: left;
  padding: 0.6rem 0.9rem;
  border: none;
  background: ${({ $active }) => ($active ? rgba(colors.primary, 0.1) : 'transparent')};
  color: ${({ $active }) => ($active ? colors.primary500 : colors.gray800)};
  font-size: ${fontSize.sm};
  font-weight: ${({ $active }) => ($active ? fontWeight.bold : fontWeight.normal)};
  cursor: pointer;
  transition: 0.15s ease;

  &:hover {
    background: ${rgba(colors.primary, 0.07)};
  }
`;

export const FolderPickerRemoveItem = styled.button`
  display: block;
  width: 100 %;
  text-align: left;
  padding: 0.6rem 0.9rem;
  border: none;
  border-top: 1px solid ${colors.gray300};
  background: transparent;
  color: ${colors.danger};
  font-size: ${fontSize.sm};
  cursor: pointer;
  transition: 0.15s ease;

  &:hover {
    background: ${rgba(colors.danger, 0.06)};
  }
`;

export const FolderPickerEmpty = styled.div`
  padding: 0.75rem 0.9rem;
  color: ${colors.gray600};
  font-size: ${fontSize.sm};
  text-align: center;
`;

export const FolderPickerBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 40;
`;

export const GroupTitle = styled.span`
  font-family: "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif;
`;